import { useState, useEffect } from 'react'
import { api } from '@/services/api'
import { Loader2, Download, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface PDFViewerProps {
  orgId: string
  fileId: string
  fileName: string
  onDownload?: () => void
}

export function PDFViewer({ orgId, fileId, fileName, onDownload }: PDFViewerProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    let url: string | null = null

    const loadPdf = async () => {
      setLoading(true)
      setError(null)
      try {
        const blob = await api.files.downloadBlob(orgId, fileId, { inline: true })
        if (cancelled) return
        url = URL.createObjectURL(blob)
        setBlobUrl(url)
      } catch (e) {
        if (cancelled) return
        setError((e as Error).message || 'Failed to load PDF')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadPdf()

    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [orgId, fileId])

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [blobUrl])

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading PDF...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="outline" onClick={onDownload}>
          <Download className="h-4 w-4 mr-2" />
          Download Instead
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-2 border-b bg-muted/50">
        <span className="text-sm font-medium truncate flex-1 mr-4">{fileName}</span>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => blobUrl && window.open(blobUrl, '_blank')}>
            <ExternalLink className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDownload}>
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        {blobUrl && (
          <iframe
            src={blobUrl}
            className="w-full h-full border-0"
            title={fileName}
          />
        )}
      </div>
    </div>
  )
}
